import React, { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Card, EmptyState, Field } from "../components/ui-ext";
import { Plus, X, Loader2, Trash2, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";

export default function ActivityTypesPage() {
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/activity-types").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const toggle = async (item) => {
    setBusy(true);
    try {
      await api.patch(`/activity-types/${item.id}`, { active: !item.active });
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const del = async (item) => {
    if (!window.confirm(`Delete "${item.name}"? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/activity-types/${item.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Master Data"
        title="Activity Types"
        subtitle='Manage the multi-select list shown in Task creation under "Activity Planned".'
        right={
          <button onClick={() => setShow(true)} className="bg-[#FF5722] hover:bg-[#F4511E] px-4 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="new-at-btn">
            <Plus className="w-4 h-4" /> New Activity Type
          </button>
        }
      />
      <Card>
        {items.length === 0 ? (
          <EmptyState title="No activity types yet" hint="Add the first activity type (e.g. I&C, NI, AT, Patching...)" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full tech-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Status</th>
                  <th>Created</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map((a) => (
                  <tr key={a.id} data-testid={`at-row-${a.id}`}>
                    <td className="font-medium text-white">{a.name}</td>
                    <td>
                      <button
                        onClick={() => toggle(a)}
                        disabled={busy}
                        data-testid={`at-toggle-${a.id}`}
                        className={`flex items-center gap-2 mono text-[10px] uppercase tracking-widest ${a.active ? "text-green-400" : "text-slate-500"}`}
                      >
                        {a.active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                        {a.active ? "Active" : "Disabled"}
                      </button>
                    </td>
                    <td className="mono text-xs text-slate-500">{a.created_at?.slice(0, 10)}</td>
                    <td className="text-right">
                      <button onClick={() => del(a)} disabled={busy} className="text-slate-500 hover:text-red-400" data-testid={`at-del-${a.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      {show && <ActivityTypeDialog onClose={() => setShow(false)} onCreated={() => { setShow(false); load(); }} />}
    </div>
  );
}

function ActivityTypeDialog({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!name.trim()) return toast.error("Name required");
    setBusy(true);
    try {
      await api.post("/activity-types", { name });
      toast.success("Added");
      onCreated();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[#13151A] border border-white/10 w-full max-w-sm">
        <div className="px-6 py-4 border-b border-white/10 flex justify-between">
          <h2 className="text-lg font-semibold">New Activity Type</h2>
          <button onClick={onClose}><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6">
          <Field label="Name">
            <input
              className="input"
              placeholder="e.g. Patching, 400G upgradation"
              value={name}
              onChange={(e) => setName(e.target.value)}
              data-testid="at-name"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && submit()}
            />
          </Field>
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t border-white/10">
          <button onClick={onClose} className="border border-white/15 px-4 py-2 text-sm rounded-sm">Cancel</button>
          <button onClick={submit} disabled={busy} className="bg-[#FF5722] hover:bg-[#F4511E] px-5 py-2 text-sm rounded-sm flex items-center gap-2" data-testid="at-submit">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />} Add
          </button>
        </div>
        <style>{`.input { width: 100%; background: #0B0C10; border: 1px solid rgba(255,255,255,0.1); padding: 8px 12px; font-size: 13px; border-radius: 2px; color: white; }`}</style>
      </div>
    </div>
  );
}
